// Ritmo de contactos (chart por hora) + tarjetas KPI.
// Equivalente a drawRate()+kpis() del original
// (public/cabrillo/src/plantilla.html:122-132). Ver src/cabrillo/CREDITOS.md.
//
// Los NÚMEROS de las tarjetas KPI los calcula motor.kpisPorBanda()
// — este módulo solo construye el HTML/Canvas a partir de esos datos, sin
// recalcular nada (render ≠ motor).

import { bl } from '../geo.js';
import { BC, fmt, css, observarRedimension } from './render-util.js';

/**
 * @param {{rt: HTMLCanvasElement, kp: HTMLElement}} els
 * @param {ReturnType<import('../motor.js').crearAnalizador>} motor
 */
export function montar(els, motor) {
    const c = els.rt.getContext('2d');

    els.rt.onclick = (e) => {
        const { hr } = motor.modelo;
        const b = els.rt.getBoundingClientRect();
        const i = Math.floor((e.clientX - b.left - 44) / ((b.width - 50) / hr[0].length));
        if (i >= 0) {
            motor.pausar();
            motor.irA(Math.min(motor.modelo.NB - 1, i * 6 + 5));
        }
    };

    els.kp.onclick = (e) => {
        const k = e.target.closest('.cab-kc');
        if (k) motor.filtrarBanda(+k.dataset.b < 0 ? null : +k.dataset.b);
    };

    function drawRate() {
        const { bands, t0, hr } = motor.modelo;
        const { cur, bf } = motor.obtenerEstado();
        const nh = hr[0].length;
        const nb = bands.length;
        const dpr = devicePixelRatio || 1;
        const W = els.rt.clientWidth;
        if (W === 0) return; // aún sin layout válido; el ResizeObserver vuelve a llamar cuando lo tenga
        const H = 190, L = 44, R = 6, T = 6, BT = 32;
        const pw = W - L - R;
        const bw = pw / nh;
        const ch = H - T - BT;
        const fg = css(els.rt, '--fg');
        const mut = css(els.rt, '--mut');
        const line = css(els.rt, '--line');

        els.rt.style.height = H + 'px';
        els.rt.width = W * dpr;
        els.rt.height = H * dpr;
        c.setTransform(dpr, 0, 0, dpr, 0, 0);

        const tt = (i) => hr.reduce((s, a, b) => s + (bf < 0 || b == bf ? a[i] : 0), 0);
        let my = 1;
        for (let i = 0; i < nh; i++) my = Math.max(my, tt(i));

        c.font = '11px system-ui';
        c.textAlign = 'right';
        c.textBaseline = 'middle';
        c.fillStyle = mut;
        [0, 0.5, 1].forEach((f) => {
            const y = T + ch * (1 - f);
            c.strokeStyle = line;
            c.beginPath();
            c.moveTo(L, y + 0.5);
            c.lineTo(L + pw, y + 0.5);
            c.stroke();
            c.fillText(Math.round(my * f) + '/h', L - 6, y);
        });

        const hc = Math.floor(cur / 6);
        for (let i = 0; i < nh; i++) {
            let y = T + ch;
            c.globalAlpha = i <= hc ? 1 : 0.2;
            for (let b = 0; b < nb; b++) {
                if (bf >= 0 && b != bf) continue;
                const h = (hr[b][i] / my) * ch;
                c.fillStyle = BC(b);
                c.fillRect(L + i * bw + 1, y - h, Math.max(bw - 2, 1), h);
                y -= h;
            }
        }
        c.globalAlpha = 1;

        c.textBaseline = 'top';
        const st = nh > 30 ? 6 : 3;
        for (let i = 0; i <= nh; i += st) {
            const d = new Date(t0 + i * 36e5);
            const x = L + i * bw;
            c.fillStyle = mut;
            c.textAlign = 'center';
            c.fillText(d.getUTCHours() + 'h', x, H - BT + 4);
            if (d.getUTCHours() == 0 || i == 0) {
                c.fillStyle = fg;
                c.textAlign = 'left';
                c.fillText(String(d.getUTCDate()).padStart(2, '0') + '/' + String(d.getUTCMonth() + 1).padStart(2, '0'), x + 2, H - BT + 17);
            }
        }

        c.strokeStyle = css(els.rt, '--acc');
        c.lineWidth = 2;
        c.strokeRect(L + hc * bw + 0.5, T, bw - 1, ch);
        c.lineWidth = 1;
    }

    function renderKpis() {
        const { bf } = motor.obtenerEstado();
        const { bands, t0 } = motor.modelo;
        const tarjetas = motor.kpisPorBanda();

        const tarjetaHtml = ({ indiceBanda, ahora, pico, horaPico, media, total }, nombre, color) =>
            `<div class="cab-kc${bf == indiceBanda ? ' on' : ''}" data-b="${indiceBanda}" style="border-left:4px solid ${color}">${nombre}<br><b>${ahora}/h</b>ahora<br>pico ${pico}/h (${fmt(t0 + horaPico * 36e5)}h) · media ${media.toFixed(1)}/h · ${total} QSO</div>`;

        els.kp.innerHTML =
            tarjetaHtml(tarjetas[0], 'Todas', 'var(--acc)') +
            bands.map((b, i) => tarjetaHtml(tarjetas[i + 1], bl(b), BC(i))).join('');
    }

    function actualizar() {
        drawRate();
        renderKpis();
    }

    actualizar();
    const desuscribir = motor.subscribe(actualizar);
    const dejarDeObservar = observarRedimension(els.rt, actualizar);

    return {
        actualizar,
        destruir() {
            desuscribir();
            dejarDeObservar();
        },
    };
}
